import { useEffect, useState } from 'react'

import { useConnect, useReduxBundlerStore } from 'redux-bundler-hook'

import { Box, CircularProgress, TextField, Typography } from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'

import * as yup from 'yup'

import { debounce, getApiFetch, isAbortError, parseApiErrors } from '@common/utils'

const debouncedSearch = debounce((fn, value) => fn(value), 500)

/**
 * FreeSoloDynamicSelect is a select component that fetches and renders a dynamic list of data and allows freesolo input.
 * @param {Object} props - The props for the component.
 * @param {string} props.label
 * @param {string} props.endpoint
 * @param {Object|string} props.value
 * @param {Function} props.onChange
 * @param {Object} [props.filters]
 * @param {string} [props.variant]
 * @param {string} [props.primaryTextAttr]
 * @param {string} [props.secondaryTextAttr]
 * @param {boolean} [props.disabled]
 * @param {string} [props.valueProp]
 * @param {string} [props.inputValueProp]
 * @param {string} [props.helperText]
 * @param {string} [props.error]
 * @param {boolean} [props.showError]
 * @param {boolean} [props.getFullEntity]
 * @param {boolean} [props.disableOptions]
 * @param {string} [props.cancelationPrefix]
 * @param {Object} [props.inputStyle]
 * @param {Object} [props.sx]
 */
export default function FreeSoloDynamicSelect({
  label,
  endpoint,
  value,
  onChange = () => {},
  filters = {},
  variant = 'standard',
  primaryTextAttr = 'name',
  secondaryTextAttr = undefined,
  disabled = false,
  valueProp = 'id',
  inputValueProp = 'name',
  helperText = '',
  error = '',
  showError = false,
  getFullEntity = false,
  disableOptions = false,
  cancelationPrefix = undefined,
  inputStyle = {},
  sx = {},
  ...rest
}) {
  const { doShowSnackbar } = useConnect('doShowSnackbar')

  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  const store = useReduxBundlerStore()
  const apiFetch = getApiFetch(store)

  const isString = (val) => typeof val === 'string'

  const callEndpoint = (params, id) => {
    const url = `/${endpoint}/`
    return apiFetch(
      id ? url.concat(id) : url,
      { pageSize: 50, ...params },
      {
        method: 'GET',
        cancelationPrefix: cancelationPrefix
          ? `free_solo_dynamic_select_${cancelationPrefix}`
          : 'free_solo_dynamic_select',
      },
    )
  }

  const handleChange = (_, newValue, reason) => {
    if (reason === 'clear') {
      onChange('clear', '')
    }
    if (reason === 'selectOption') {
      if (newValue.freeSoloInput) {
        const parsedValue = getFullEntity
          ? { id: null, [inputValueProp]: newValue.freeSoloInput }
          : newValue.freeSoloInput
        onChange('created', parsedValue)
      } else {
        const parsedValue = getFullEntity ? newValue : newValue?.[valueProp]
        onChange('existing', parsedValue)
      }
    }
    if (reason === 'createOption') {
      const existingOption = options?.find(
        (option) => option[inputValueProp] === newValue,
      )
      if (existingOption) {
        const parsedValue = getFullEntity ? existingOption : existingOption?.[valueProp]
        onChange('existing', parsedValue)
      } else {
        onChange('created', { id: null, [inputValueProp]: newValue })
      }
    }
  }

  const fetchOptions = async (text, id) => {
    setLoading(true)
    try {
      const res = !disableOptions
        ? await callEndpoint({ ...filters, search: text, active: true }, id)
        : { results: [] }
      if (res?.results) setOptions(res.results)
      if (res?.id) setOptions([res])
    } catch (err) {
      if (!isAbortError(err)) {
        const parsedError = parseApiErrors(err)
        doShowSnackbar(parsedError, 'error')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = async (event, newValue) => {
    if (!event || event?.type === 'click') return

    debouncedSearch(fetchOptions, newValue)
  }

  const handleDropDown = async () => fetchOptions(null)

  const getOptionData = (option) =>
    isString(option) ? options?.find((opt) => opt.id === option) : option

  const getOptionLabel = (option) => {
    const optData = getOptionData(option)
    return option?.freeSoloInput ?? optData?.[primaryTextAttr] ?? option
  }

  useEffect(() => {
    if (options?.length === 0 && value) {
      try {
        const uuidSchema = yup.string().uuid()
        const uuid = uuidSchema.validateSync(value)
        fetchOptions(null, uuid)
      } catch (err) {
        // do not fecth if the value is not a uuid
      }
    }
  }, [value])

  return (
    <Autocomplete
      forcePopupIcon
      selectOnFocus
      clearOnBlur
      autoHighlight
      id="free-solo-dynamic-select"
      disabled={disabled}
      value={value}
      onOpen={handleDropDown}
      getOptionLabel={getOptionLabel}
      options={options ?? []}
      onChange={handleChange}
      onInputChange={handleInputChange}
      filterOptions={(opts, params) => {
        const filtered = createFilterOptions()(opts, params)

        const { inputValue } = params
        // Suggest the creation of a new value
        const isExisting = opts.some(
          (option) =>
            inputValue === option[inputValueProp] || inputValue === option[valueProp],
        )
        if (inputValue !== '' && !isExisting) {
          filtered.push({
            freeSoloInput: inputValue,
            [inputValueProp]: `Add "${inputValue}"`,
          })
        }

        return filtered
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={rest.required}
          label={label}
          error={showError}
          helperText={showError ? error : helperText}
          placeholder={rest.placeholder}
          variant={variant}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
          sx={{ ...params.sx, ...inputStyle }}
        />
      )}
      renderOption={(optProps, option) => (
        <li {...optProps} key={option.id || option.freeSoloInput}>
          <Box>
            <Typography variant="h6">{option[primaryTextAttr]}</Typography>
            {secondaryTextAttr && (
              <Typography
                variant="body2"
                color="text.secondary"
                textTransform="uppercase"
                fontSize="0.7rem"
              >
                {option[secondaryTextAttr]}
              </Typography>
            )}
          </Box>
        </li>
      )}
      sx={sx}
    />
  )
}
